import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import LocationSelector from '../components/LocationSelector';
import Navbar from '../components/Navbar';

function Events() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('upcoming'); // upcoming, past, my-events, my-rsvps, invites
  const [events, setEvents] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [userIdentities, setUserIdentities] = useState([]);
  const [newEvent, setNewEvent] = useState({
    title: '',
    description: '',
    eventDate: '',
    endDate: '',
    locationType: 'physical',
    locationAddress: '',
    locationCity: '',
    locationState: '',
    locationCountry: 'US',
    onlineLink: '',
    maxAttendees: '',
    groupId: '',
    targetIdentityId: ''
  });

  useEffect(() => {
    fetchEvents();
    fetchGroups();
    fetchUserIdentities();
    fetchUserProfile();
  }, [activeTab]);

  // Fetch invite count on mount so the badge shows immediately
  useEffect(() => {
    fetchInviteCount();
  }, []);

  const fetchInviteCount = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/events/invites/received', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvites(response.data.invites || []);
    } catch (error) {
      // Silently fail - invite count is not critical
    }
  };

  const fetchUserProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('User profile response:', response.data);
      
      // The response.data IS the user object (not wrapped in response.data.user)
      const user = response.data;
      console.log('Setting event location from profile:', {
        city: user.current_city,
        state: user.current_state,
        country: user.current_country
      });
      
      setNewEvent(prev => ({
        ...prev,
        locationCity: user.current_city || '',
        locationState: user.current_state || '',
        locationCountry: user.current_country || 'US'
      }));
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchUserIdentities = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `/api/identities/me`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setUserIdentities(response.data.identities || []);
      
      // Set default target identity to first one
      if (response.data.identities && response.data.identities.length > 0) {
        setNewEvent(prev => ({ ...prev, targetIdentityId: response.data.identities[0].identity_id }));
      }
    } catch (error) {
      console.error('Error fetching user identities:', error);
    }
  };

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      
      if (activeTab === 'invites') {
        const response = await axios.get('/api/events/invites/received', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setInvites(response.data.invites);
      } else {
        const response = await axios.get(`/api/events?filter=${activeTab}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setEvents(response.data.events);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setMessage('Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get('/api/groups?filter=my-groups', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data.groups);
    } catch (error) {
      console.error('Error fetching groups:', error);
    }
  };

  const handleCreateEvent = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('accessToken');
      
      // Build event data, only include fields that have values
      const eventData = {
        title: newEvent.title,
        description: newEvent.description || undefined,
        eventDate: new Date(newEvent.eventDate).toISOString(),
        locationType: newEvent.locationType,
        locationAddress: newEvent.locationAddress || undefined,
        locationCity: newEvent.locationCity || undefined,
        locationState: newEvent.locationState || undefined,
        onlineLink: newEvent.onlineLink || undefined,
        maxAttendees: newEvent.maxAttendees ? parseInt(newEvent.maxAttendees) : undefined,
        groupId: newEvent.groupId ? parseInt(newEvent.groupId) : undefined,
        targetIdentityId: newEvent.targetIdentityId ? parseInt(newEvent.targetIdentityId) : undefined
      };
      
      // Only add endDate if it has a value
      if (newEvent.endDate) {
        eventData.endDate = new Date(newEvent.endDate).toISOString();
      }
      
      await axios.post('/api/events', eventData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Event created successfully!');
      setShowCreateModal(false);
      
      // Reset form but keep user's current location as default
      const userProfile = await axios.get('/api/users/me', {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = userProfile.data; // response.data IS the user object
      
      setNewEvent({
        title: '',
        description: '',
        eventDate: '',
        endDate: '',
        locationType: 'physical',
        locationAddress: '',
        locationCity: user.current_city || '',
        locationState: user.current_state || '',
        locationCountry: user.current_country || 'US',
        onlineLink: '',
        maxAttendees: '',
        groupId: '',
        targetIdentityId: userIdentities.length > 0 ? userIdentities[0].identity_id : ''
      });
      fetchEvents();
    } catch (error) {
      console.error('Create event error:', error.response?.data);
      const errorMsg = error.response?.data?.errors 
        ? error.response.data.errors.map(e => e.msg).join(', ')
        : error.response?.data?.error || 'Failed to create event';
      setMessage(errorMsg);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleOpenCreateModal = () => {
    console.log('Opening create modal with current event state:', newEvent);
    setShowCreateModal(true);
  };

  const handleAcceptInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/events/invites/${inviteId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('✅ Invite accepted! Event added to "My RSVPs"');
      fetchEvents();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to accept invite');
    }
  };

  const handleDeclineInvite = async (inviteId) => {
    try {
      const token = localStorage.getItem('accessToken');
      await axios.put(`/api/events/invites/${inviteId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessage('Invite declined');
      fetchEvents();
    } catch (error) {
      setMessage(error.response?.data?.error || 'Failed to decline invite');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isUpcoming = (dateString) => {
    return new Date(dateString) >= new Date();
  };

  return (
    <div>
      <Navbar />

      <div className="container">
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
            <h2 style={{ flex: '1 1 auto', minWidth: '150px' }}>What's happening</h2>
            <button 
              onClick={handleOpenCreateModal}
              className="btn btn-primary"
              style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Plan
            </button>
          </div>
          
          <div style={{ display: 'flex', gap: '8px', marginTop: '20px', flexWrap: 'wrap' }}>
            <button 
              onClick={() => setActiveTab('upcoming')}
              className={activeTab === 'upcoming' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              New
            </button>
            <button 
              onClick={() => setActiveTab('past')}
              className={activeTab === 'past' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Past
            </button>
            <button 
              onClick={() => setActiveTab('my-events')}
              className={activeTab === 'my-events' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Mine
            </button>
            <button 
              onClick={() => setActiveTab('my-rsvps')}
              className={activeTab === 'my-rsvps' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Going
            </button>
            <button 
              onClick={() => setActiveTab('invites')}
              className={activeTab === 'invites' ? 'btn btn-primary' : 'btn btn-secondary'}
              style={{ fontSize: '13px', padding: '8px 12px' }}
            >
              Invites {invites.length > 0 && `(${invites.length})`}
            </button>
          </div>
        </div>

        {message && (
          <div className="card" style={{
            backgroundColor: message.includes('✅') || message.includes('success') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') || message.includes('success') ? '#155724' : '#721c24'
          }}>
            {message}
          </div>
        )}

        {loading ? (
          <div className="card">Loading events...</div>
        ) : activeTab === 'invites' ? (
          invites.length === 0 ? (
            <div className="card">
              <p>No invites yet!</p>
            </div>
          ) : (
            invites.map((invite) => (
              <div key={invite.invite_id} className="card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', flexWrap: 'wrap', gap: '16px' }}>
                  <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3>{invite.event_title}</h3>
                    
                    {invite.event_description && (
                      <p style={{ marginTop: '8px', color: '#666' }}>
                        {invite.event_description.length > 150 
                          ? invite.event_description.substring(0, 150) + '...' 
                          : invite.event_description}
                      </p>
                    )}
                    
                    {invite.target_identity && (
                      <div style={{ marginTop: '8px' }}>
                        <span style={{ 
                          padding: '2px 8px',
                          backgroundColor: '#e0f2fe',
                          color: '#0369a1',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          {invite.target_identity.icon} {invite.target_identity.category_display}
                        </span>
                      </div>
                    )}
                    
                    <div style={{ marginTop: '12px', fontSize: '14px', color: '#999' }}>
                      <div>
                        📅 {formatDate(invite.event_date)}
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        {invite.location_type === 'online' ? '💻' : invite.location_type === 'hybrid' ? '🌐' : '📍'} 
                        {' '}
                        {invite.location_type === 'online' ? 'Online Event' : 
                         invite.location_city ? `${invite.location_city}, ${invite.location_state}` : 'Location TBD'}
                      </div>
                      <div style={{ marginTop: '4px' }}>
                        👥 {parseInt(invite.going_count) || 0} going, {parseInt(invite.interested_count) || 0} interested
                      </div>
                      <p style={{ marginTop: '8px' }}>
                        Invited by <strong>{invite.inviter_first_name} {invite.inviter_last_name}</strong>
                      </p>
                      <p style={{ marginTop: '4px' }}>
                        Invited {new Date(invite.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button 
                      onClick={() => handleAcceptInvite(invite.invite_id)}
                      className="btn btn-primary"
                    >
                      Count me in!
                    </button>
                    <button 
                      onClick={() => handleDeclineInvite(invite.invite_id)}
                      className="btn btn-secondary"
                    >
                      Can't make it
                    </button>
                  </div>
                </div>
              </div>
            ))
          )
        ) : events.length === 0 ? (
          <div className="card">
            <p>Nothing happening yet!</p>
            {activeTab === 'upcoming' && (
              <p style={{ color: '#666' }}>
                Plan something fun or check back later!
              </p>
            )}
          </div>
        ) : (
          events.map((event) => (
            <div key={event.event_id} className="card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <h3>{event.title}</h3>
                    
                    {event.is_cancelled && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        Cancelled
                      </span>
                    )}
                    
                    {event.group_name && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        📁 {event.group_name}
                      </span>
                    )}
                    
                    {event.my_rsvp_status && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: event.my_rsvp_status === 'going' ? '#28a745' : '#ffc107',
                        color: event.my_rsvp_status === 'going' ? 'white' : '#000',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {event.my_rsvp_status === 'going' ? '✓ Going' : '★ Interested'}
                      </span>
                    )}
                    
                    {event.target_identity && (
                      <span style={{
                        padding: '2px 8px',
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}>
                        {event.target_identity.icon} {event.target_identity.category_display}
                      </span>
                    )}
                  </div>
                  
                  {event.description && (
                    <p style={{ marginTop: '8px', color: '#666' }}>
                      {event.description.length > 150 
                        ? event.description.substring(0, 150) + '...' 
                        : event.description}
                    </p>
                  )}
                  
                  <div style={{ marginTop: '12px', fontSize: '14px', color: '#999' }}>
                    <div>
                      📅 {formatDate(event.event_date)}
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      {event.location_type === 'online' ? '💻' : event.location_type === 'hybrid' ? '🌐' : '📍'} 
                      {' '}
                      {event.location_type === 'online' ? 'Online Event' : 
                       event.location_city ? `${event.location_city}, ${event.location_state}` : 'Location TBD'}
                    </div>
                    <div style={{ marginTop: '4px' }}>
                      👥 {parseInt(event.going_count) || 0} going, {parseInt(event.interested_count) || 0} interested
                      {event.max_attendees && ` (max ${event.max_attendees})`}
                    </div>
                  </div>
                </div>
                
                <button 
                  onClick={() => navigate(`/events/${event.event_id}`)}
                  className="btn btn-primary"
                  style={{ marginLeft: '16px' }}
                >
                  See details
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          overflow: 'auto'
        }}>
          <div className="card" style={{ 
            maxWidth: '600px', 
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
            margin: '20px'
          }}>
            <h2>Plan something fun</h2>
            
            <form onSubmit={handleCreateEvent}>
              <div style={{ marginTop: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Event Title *
                </label>
                <input
                  type="text"
                  value={newEvent.title}
                  onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                  required
                  minLength={3}
                  maxLength={200}
                  placeholder="e.g., Community Meetup"
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                />
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Description
                </label>
                <textarea
                  value={newEvent.description}
                  onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                  placeholder="What's this event about?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px',
                    resize: 'vertical'
                  }}
                />
              </div>

              <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Event Date & Time *
                  </label>
                  <input
                    type="datetime-local"
                    value={newEvent.eventDate}
                    onChange={(e) => setNewEvent({ ...newEvent, eventDate: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    End Date & Time
                  </label>
                  <input
                    type="datetime-local"
                    value={newEvent.endDate}
                    onChange={(e) => setNewEvent({ ...newEvent, endDate: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Location Type *
                </label>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="physical"
                      checked={newEvent.locationType === 'physical'}
                      onChange={(e) => setNewEvent({ ...newEvent, locationType: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    📍 Physical
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="online"
                      checked={newEvent.locationType === 'online'}
                      onChange={(e) => setNewEvent({ ...newEvent, locationType: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    💻 Online
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      value="hybrid"
                      checked={newEvent.locationType === 'hybrid'}
                      onChange={(e) => setNewEvent({ ...newEvent, locationType: e.target.value })}
                      style={{ marginRight: '6px' }}
                    />
                    🌐 Hybrid
                  </label>
                </div>
              </div>

              {(newEvent.locationType === 'physical' || newEvent.locationType === 'hybrid') && (
                <>
                  <div style={{ marginTop: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                      Address
                    </label>
                    <input
                      type="text"
                      value={newEvent.locationAddress}
                      onChange={(e) => setNewEvent({ ...newEvent, locationAddress: e.target.value })}
                      placeholder="Street address"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '16px' }}>
                    <LocationSelector
                      country={newEvent.locationCountry}
                      state={newEvent.locationState}
                      city={newEvent.locationCity}
                      onCountryChange={(country) => setNewEvent({ ...newEvent, locationCountry: country, locationState: '', locationCity: '' })}
                      onStateChange={(state) => setNewEvent({ ...newEvent, locationState: state, locationCity: '' })}
                      onCityChange={(city) => setNewEvent({ ...newEvent, locationCity: city })}
                      required={false}
                    />
                  </div>
                </>
              )}

              {(newEvent.locationType === 'online' || newEvent.locationType === 'hybrid') && (
                <div style={{ marginTop: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Online Link
                  </label>
                  <input
                    type="url"
                    value={newEvent.onlineLink}
                    onChange={(e) => setNewEvent({ ...newEvent, onlineLink: e.target.value })}
                    placeholder="https://zoom.us/..."
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
              )}

              <div style={{ marginTop: '16px', display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Max Attendees
                  </label>
                  <input
                    type="number"
                    value={newEvent.maxAttendees}
                    onChange={(e) => setNewEvent({ ...newEvent, maxAttendees: e.target.value })}
                    placeholder="Leave empty for unlimited"
                    min="1"
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Group (Optional)
                  </label>
                  <select
                    value={newEvent.groupId}
                    onChange={(e) => setNewEvent({ ...newEvent, groupId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px'
                    }}
                  >
                    <option value="">Public Event</option>
                    {groups.map(group => (
                      <option key={group.group_id} value={group.group_id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: '16px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                  Identity Community *
                </label>
                <select
                  value={newEvent.targetIdentityId}
                  onChange={(e) => setNewEvent({ ...newEvent, targetIdentityId: e.target.value })}
                  required
                  style={{
                    width: '100%',
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '16px'
                  }}
                >
                  {userIdentities.length === 0 ? (
                    <option value="">No identities found</option>
                  ) : (
                    userIdentities.map(identity => (
                      <option key={identity.identity_id} value={identity.identity_id}>
                        {identity.category_icon} {identity.category_display_name}: {identity.identity_name}
                      </option>
                    ))
                  )}
                </select>
                <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                  Only members with this identity will see this event
                </div>
              </div>

              <div style={{ marginTop: '24px', display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="btn btn-secondary"
                >
                  Nah, go back
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                >
                  Create 🎉
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Events;

